import { Currency } from "@goodfill/models-typescript";
import { DOCUMENT_UPLOAD_TYPE } from "../../backendApi/lambdas/individualApplication/uploadDocumentForApplicationForm";
import { UserId } from "@goodfill/domain-properties";
import {
  Employment,
  Phone,
  Address,
  PersonalInformation,
  AccountInformation,
  AffiliationDetails,
  CustomerApplication,
  FinancialInformation,
  RegulatoryInformation,
  SourceOfWealth,
  EmploymentDetails,
  RecognizedEmployerAffiliationDetails,
  Relationship,
  CustomerApplicationDeepPartial,
} from "@goodfill/domain-customer-application-types";
import {
  BrokerCompany,
  CustomerApplicationFormState,
  CustomerApplicationFormStateBroker,
  CustomerApplicationFormStateEmployment,
  REQUIRED_EMPLOYMENT_DETAILS,
  selectedRecognizedEmployerTransform,
} from "./formState/formState";
import { AddressWithCountryScheme, AddressWithoutCountryScheme } from "../../../form/zod/formAddress";
import { ValidStep1SignUpState } from "./form/steps/Step1SignUp/step1SignUpState";
import { deserializePhone } from "../../../form/zod/formPhone";
import { DefaultValues } from "react-hook-form";

const mapFormAddressWithoutCountryToApiAddress = (
  address: DefaultValues<AddressWithoutCountryScheme>,
): CustomerApplicationDeepPartial<Address> => {
  return {
    street1: address.street1,
    ...(address.street2 ? { street2: address.street2 } : {}),
    city: address.city,
    state: address.state,
    zipCode: address.zipCode,
    country: { countryIsoCode: "USA" }, // intentionally, hardcoded USA
  };
};

const mapFormAddressWithCountryToApiAddress = (
  address: DefaultValues<AddressWithCountryScheme>,
): CustomerApplicationDeepPartial<Address> => {
  return {
    ...mapFormAddressWithoutCountryToApiAddress(address),
    country: { countryIsoCode: address.country },
  };
};

function mapEmploymentDetailsToApi(
  employment: DefaultValues<CustomerApplicationFormStateEmployment>,
): CustomerApplicationDeepPartial<EmploymentDetails> | undefined {
  return {
    employer: employment.employer?.name,
    occupation: employment.occupation?.occupation,
    employerBusiness: employment.occupation?.business,
    employerCountry: {
      countryIsoCode: employment.employer?.country,
    },
    ...(employment.employer?.abroadExplanation
      ? { employerCountryAbroadExplanation: employment.employer.abroadExplanation }
      : {}),
  };
}

function mapEmploymentToApi(
  state: DefaultValues<CustomerApplicationFormState>,
): CustomerApplicationDeepPartial<Employment> {
  return {
    status: state.step4?.employmentStatus,
    details:
      state.step4?.employmentStatus &&
      REQUIRED_EMPLOYMENT_DETAILS.includes(state.step4.employmentStatus) &&
      state.step4?.employment
        ? mapEmploymentDetailsToApi(state.step4?.employment)
        : undefined,
  };
}

export function mapPhonesToApi(step1: ValidStep1SignUpState): Phone[] {
  const [countryCallCode, nationalNumber] = deserializePhone(step1.phone1);

  return [{ countryCallingCode: countryCallCode, number: nationalNumber }];
}

function mapPersonalInformationToApi(
  state: DefaultValues<CustomerApplicationFormState>,
  step1: ValidStep1SignUpState,
): CustomerApplicationDeepPartial<PersonalInformation> {
  return {
    name: {
      first: step1?.firstName,
      last: step1?.lastName,
    },
    dateOfBirth: state.step2?.dateOfBirth,
    countryOfBirth: {
      countryIsoCode: state.step3?.countryOfBirth,
    },
    maritalStatus: state.step2?.maritalStatus,
    numberOfDependents: state.step2?.numberOfDependents,
    residence: state.step2?.address ? mapFormAddressWithoutCountryToApiAddress(state.step2.address) : undefined,
    ...(!state.step2?.sameMailingAddress && state.step2?.mailingAddress
      ? {
          mailingAddress: mapFormAddressWithoutCountryToApiAddress(state.step2.mailingAddress),
        }
      : {}),
    phones: mapPhonesToApi(step1),
    email: step1.email,
    hasUSLegalResidence: state.step3?.usaTaxResidency,
    hasUSTaxResidence: true, // hardcoded
    socialSecurityNumber: state.step3?.socialSecurityNumber,
    citizenships: [{ countryIsoCode: state.step3?.countryOfCitizenship }],
    employment: mapEmploymentToApi(state),
    issuingCountryOfIdentificationDocument: { countryIsoCode: state.step2?.issuingCountryOfIdentification },
  };
}

function mapSourcesOfWealthToApi(
  sourcesOfWealth: DefaultValues<CustomerApplicationFormState["step5"]["sourcesOfWealth"]>,
): CustomerApplicationDeepPartial<SourceOfWealth>[] {
  return sourcesOfWealth.map((sourceOfWealth): CustomerApplicationDeepPartial<SourceOfWealth> => {
    return {
      type: sourceOfWealth?.wealthType,
      frequency: sourceOfWealth?.frequency,
      isUsedForFunds: sourceOfWealth?.isUsedForFunds,
      ...(sourceOfWealth?.description
        ? {
            description: sourceOfWealth.description,
          }
        : {}),
    };
  });
}

function mapFinancialInformationToApi(
  state: DefaultValues<CustomerApplicationFormState>,
): CustomerApplicationDeepPartial<FinancialInformation> {
  return {
    netWorth: { amount: `${state.step5?.netWorth}`, currency: Currency.USD },
    liquidNetWorth: { amount: `${state.step5?.liquidNetWorth}`, currency: Currency.USD },
    annualNetIncome: { amount: `${state.step5?.annualNetIncome}`, currency: Currency.USD },
    investmentExperience: {
      bonds: {
        knowledgeLevel: state.step6?.bondsExperienceLevel,
        tradesPerYear: state.step6?.tradesPerYearBonds,
        yearsTrading: state.step6?.yearsExperienceBonds,
      },
    },
    sourcesOfWealth: state.step5?.sourcesOfWealth ? mapSourcesOfWealthToApi(state.step5?.sourcesOfWealth) : [],
  };
}

function mapAccountInformationToApi(
  state: DefaultValues<CustomerApplicationFormState>,
): CustomerApplicationDeepPartial<AccountInformation> {
  return {
    brokerFormsAgreementSignature: "NON_EMPTY_STRING_TO_BYPASS_VALIDATION",
    investmentObjectives: state.step5?.investmentObjectives,
    permissions: {
      allowBondTrading: state.step8?.allowBondTrading,
      allowChangingAccountInformationAfterUserInstruction:
        state.step8?.allowChangingAccountInformationAfterUserInstruction,
      allowTransmittingReceivedOrders: state.step8?.allowTransmittingReceivedOrders,
      allowConfirmingAccountInformation: state.step8?.allowConfirmAccountInformation,
      allowRespondingToTenders: state.step8?.allowRespondingToTenders,
      allowRespondingToPutOptionsContingentUponCorporateAction:
        state.step8?.allowRespondingToPutOptionsContingentUponCorporateAction,
      allowRespondingToPutOptionsNotContingentUponCorporateAction:
        state.step8?.allowRespondingToPutOptionsNotContingentUponCorporateAction,
      allowOtherElectionsByBondHolders: state.step8?.allowOtherElectionsByBondHolders,
    },
    acknowledgements: {
      agreeClientAgreement: true, // this is agreed implicitly by continuing
      agreePrivacyPolicy: true, // this is agreed implicitly by continuing
    },
  };
}

function mapAffiliationDetailsToApi(
  broker: DefaultValues<CustomerApplicationFormStateBroker & { brokerCompany: BrokerCompany }>,
): CustomerApplicationDeepPartial<AffiliationDetails> {
  const [countryCallCode, nationalNumber] = deserializePhone(broker.brokerCompany?.phone);

  return {
    isDuplicateStatementRequired: broker.duplicateStatementRequired,
    relationship: broker.brokerAffiliationRelationship,
    affiliatedPersonFullName: broker.brokerName,
    company: {
      name: broker.brokerCompany?.name,
      phone: {
        countryCallingCode: countryCallCode,
        number: nationalNumber,
      },
      email: broker.brokerCompany?.email,
      address: broker.brokerCompany?.address
        ? mapFormAddressWithoutCountryToApiAddress(broker.brokerCompany.address)
        : undefined,
    },
  };
}

const mapRecognizedAffiliationDetailsToApi = (
  step1: ValidStep1SignUpState,
  broker: DefaultValues<CustomerApplicationFormStateBroker>,
): CustomerApplicationDeepPartial<RecognizedEmployerAffiliationDetails> => {
  return {
    isDuplicateStatementRequired: broker.duplicateStatementRequired,
    // TODO: Remove this type casting once backend API types properly reflect the ability to
    //  clear field using null:
    //  Api typings: https://github.com/Goodfill/js-packages/blob/main/packages/domain-customer-application-types/src/subtypes/affiliation.ts#L10-L22
    //  Discovery of type discrepancy: https://github.com/Goodfill/web-trading-platform/pull/257#discussion_r1055270469
    //  Decision: https://goodfillworkspace.slack.com/archives/D048BCYTKU4/p1671702851337049
    relationship: (broker.brokerAffiliationRelationship ?? null) as Relationship,
    affiliatedPersonFullName:
      broker.brokerAffiliationRelationship === "self" ? `${step1.firstName} ${step1.lastName}` : broker.brokerName,
    brokerRecognizedEmployer: selectedRecognizedEmployerTransform.toObject(broker.selectedRecognizedEmployer)!,
  };
};

function mapRegulatoryInformationToApi(
  state: DefaultValues<CustomerApplicationFormState>,
  step1: ValidStep1SignUpState,
): CustomerApplicationDeepPartial<RegulatoryInformation> {
  const hasRecognizedEmployer = Boolean(
    selectedRecognizedEmployerTransform.toObject(state.step7?.broker?.selectedRecognizedEmployer ?? "")?.employerId,
  );

  return {
    affiliation: {
      isAffiliated: state?.step7?.isBroker,
      details:
        state.step7?.isBroker &&
        state.step7?.broker !== null &&
        state.step7?.broker?.brokerCompany !== null &&
        !hasRecognizedEmployer
          ? // Spread required to satisfy type-guard
            mapAffiliationDetailsToApi({ ...state.step7.broker, brokerCompany: state.step7?.broker?.brokerCompany })
          : undefined,
      recognizedEmployerDetails:
        state.step7?.isBroker && state.step7?.broker && hasRecognizedEmployer
          ? mapRecognizedAffiliationDetailsToApi(step1, state.step7?.broker)
          : undefined,
    },
    employeeOfPubliclyTradedCompany: {
      isEmployeeOfPubliclyTradedCompany: state.step7?.isEmployeeOfPubliclyTradedCompany,
      companyStockSymbols: state.step7?.employeeOfPubliclyTradedCompanyStockSymbols,
    },
    controlOfPubliclyTradedCompany: {
      hasControlOfPubliclyTradedCompany: state.step7?.hasControlOfPubliclyTradedCompany,
      companyStockSymbols: state.step7?.controlOfPubliclyTradedCompanyStockSymbols,
    },
  };
}

export function mapCustomerApplicationStateToApi(
  state: DefaultValues<CustomerApplicationFormState>,
  step1: ValidStep1SignUpState,
  userId: UserId,
): CustomerApplicationDeepPartial<CustomerApplication> {
  const documents: Array<{ documentId: string; type: DOCUMENT_UPLOAD_TYPE }> = [];
  if (state.step2?.proofOfAddressFileId) {
    documents.push({
      documentId: state.step2.proofOfAddressFileId,
      type: "ProofOfAddress",
    });
  }

  if (state.step2?.proofOfIdentityFileFrontId) {
    documents.push({
      documentId: state.step2.proofOfIdentityFileFrontId,
      type: "ProofOfIdentity",
    });
  }

  if (state.step2?.proofOfIdentityFileBackId) {
    documents.push({
      documentId: state.step2.proofOfIdentityFileBackId,
      type: "ProofOfIdentity",
    });
  }

  return {
    submissionId: undefined,
    userId: `${userId}`,
    personalInformation: mapPersonalInformationToApi(state, step1),
    financialInformation: mapFinancialInformationToApi(state),
    accountInformation: mapAccountInformationToApi(state),
    regulatoryInformation: mapRegulatoryInformationToApi(state, step1),
    documents,
  };
}
